import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Camera, Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { InsertProject, Project } from "@shared/schema";

const mobileCreateSchema = z.object({
  name: z.string().min(1, "Bitte einen Namen eingeben"),
  address: z.string().optional(),
  description: z.string().optional(),
});

type MobileCreateForm = z.infer<typeof mobileCreateSchema>;

export function MobileProjectCreate() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<MobileCreateForm>({
    resolver: zodResolver(mobileCreateSchema),
    defaultValues: { name: "", address: "", description: "" },
  });

  const createMutation = useMutation({
    mutationFn: async (data: MobileCreateForm): Promise<Project> => {
      const payload: InsertProject = {
        name: data.name,
        type: "",
        customer: "",
        address: data.address ?? "",
        description: data.description ?? "",
        status: "draft",
        currentStep: 1,
        objektbeschaffenheit: [],
        massnahmen: [],
        mengeneinheit: "m²",
      };
      const res = await apiRequest("POST", "/api/projects", payload);
      return (await res.json()) as Project;
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      toast({
        title: "Projekt angelegt",
        description: "Du kannst jetzt Fotos aufnehmen.",
      });
      navigate(`/project/edit/${project.id}`);
    },
    onError: () => {
      toast({
        title: "Fehler beim Anlegen",
        description: "Das Projekt konnte nicht erstellt werden.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: MobileCreateForm) => {
    createMutation.mutate(data);
  };

  return (
    <div className="px-4 py-5 max-w-xl mx-auto">
      <Button
        type="button"
        variant="ghost"
        onClick={() => navigate("/dashboard")}
        className="mb-3 -ml-2 py-2.5 h-auto text-gray-700"
        data-testid="button-mobile-create-back"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" />
        Zurück
      </Button>

      <h1 className="text-2xl font-bold text-bauwerk-teal mb-1">
        Neues Projekt
      </h1>
      <p className="text-sm text-gray-600 mb-5">
        Nur das Nötigste – Details kannst du später am Computer ergänzen.
      </p>

      <Card>
        <CardContent className="p-4">
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <div className="space-y-1.5">
              <Label htmlFor="m-name" className="text-sm font-medium">
                Projektname *
              </Label>
              <Input
                id="m-name"
                placeholder="z. B. Wohnhaus Musterstraße"
                autoFocus
                inputMode="text"
                className="h-12 text-base"
                {...form.register("name")}
                data-testid="input-mobile-project-name"
              />
              {form.formState.errors.name && (
                <p className="text-sm text-red-600">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="m-address" className="text-sm font-medium">
                Ort / Adresse{" "}
                <span className="text-xs text-gray-500 font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                id="m-address"
                placeholder="z. B. Hauptstraße 1, Wien"
                className="h-12 text-base"
                {...form.register("address")}
                data-testid="input-mobile-project-address"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="m-description" className="text-sm font-medium">
                Kurze Notiz{" "}
                <span className="text-xs text-gray-500 font-normal">
                  (optional)
                </span>
              </Label>
              <Textarea
                id="m-description"
                placeholder="z. B. Sockelschäden, Risse an Westseite"
                rows={3}
                className="text-base"
                {...form.register("description")}
                data-testid="textarea-mobile-project-description"
              />
            </div>

            <Button
              type="submit"
              disabled={createMutation.isPending}
              className="w-full bg-bauwerk-orange hover:bg-bauwerk-light-orange py-4 h-auto text-base mt-2"
              data-testid="button-mobile-create-submit"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Speichern…
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5 mr-2" />
                  Anlegen & Fotos aufnehmen
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
